import MapboxDraw, { DrawCustomModeThis, DrawFeature, DrawPoint } from "@mapbox/mapbox-gl-draw";
import { Feature, Point } from "geojson";
import { defer } from "lodash";

import { DRAWING_CONSTANTS } from "fond/constants";
import { EditingLayer } from "fond/types";

import { NetworkTopologyConfig, pullInFeatures, refreshSnappedStatus } from "../topologyHelper";

const { constants: Constants } = MapboxDraw;

/**
 * Makes the onSplit callback for underground paths.
 *
 * Called by the lineSplit mode after an underground path is split.
 * Creates underground points at each split intersection point,
 * skipping locations where a point or pole already exists.
 */
export const makeOnSplitUndergroundPath = (config: NetworkTopologyConfig): NonNullable<EditingLayer["onSplit"]> =>
  function onSplitUndergroundPath(
    this: DrawCustomModeThis,
    _originalFeature: DrawFeature,
    _newFeatures: DrawFeature[],
    splitPoints: Feature<Point>[]
  ): void {
    const newPoints: DrawPoint[] = [];

    splitPoints.forEach((splitPoint) => {
      const { coordinates } = splitPoint.geometry;

      const tempPoint = this.newFeature({
        type: Constants.geojsonTypes.FEATURE,
        properties: { layerId: config.underground.pointLayerId, [DRAWING_CONSTANTS.Unsnapped]: "false" },
        geometry: {
          type: Constants.geojsonTypes.POINT,
          coordinates,
        },
      }) as DrawPoint;

      // Check for existing underground points at this location
      const { points: existingUgPoints } = pullInFeatures.call(this, config.underground, tempPoint);

      // Check for existing poles at this location
      const { points: existingPoles } = pullInFeatures.call(this, config.aerial, tempPoint);

      if (existingUgPoints.length > 0 || existingPoles.length > 0) {
        // Point already exists at this location — don't create duplicate
        this.deleteFeature(tempPoint.id as string, { silent: true });
      } else {
        this.addFeature(tempPoint);
        newPoints.push(tempPoint);
        refreshSnappedStatus.call(this, config.underground, tempPoint);
      }
    });

    if (newPoints.length > 0) {
      // Defer setting the id property to match the MapboxDraw-assigned id
      defer(() => {
        newPoints.forEach((p) => {
          p.setProperty("id", p.id);
        });
      });
    }
  };
